import React, { useEffect, useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DocumentService from '../../Services/documents.service';
import { XtramButton, XtramNotice } from '../Widgets/XtramModal/XtramModal';
import styles from './PCNXtram.module.scss';

const toMs = (value) => {
    if (value == null || value === '') return null;
    const ms = Number(value.valueOf());
    return Number.isNaN(ms) ? null : ms;
};

/**
 * Income reference continuity for start..end (Date or dayjs) while `open`. Equal dates of a new
 * object do not refetch; `delay` debounces quick period changes.
 */
export const useReferenceContinuity = ({ open, caseId, token, start, end, delay = 0 }) => {
    const [result, setResult] = useState(null);
    const startMs = toMs(start);
    const endMs = toMs(end);

    useEffect(() => {
        if (!open || !caseId || !token || startMs == null || endMs == null) {
            setResult(null);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const data = await DocumentService.CheckReferenceContinuity(caseId, new Date(startMs), new Date(endMs), token);
                if (!cancelled) setResult(data);
            } catch (error) {
                console.error('Error checking reference continuity:', error);
                if (!cancelled) setResult(null);
            }
        }, delay);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, caseId, token, startMs, endMs, delay]);

    return result;
};

const describeIssue = (issue, t) => {
    if (issue.issueType === 'Duplicate') return `${t('Duplicate reference number')}: ${issue.actualReference}`;
    if (issue.issueType === 'Gap') return `${t('Missing reference number')}: ${issue.expectedReference}`;
    if (issue.issueType === 'Invalid') return `${t('Invalid reference format')}: '${issue.actualReference}'`;
    return issue.description;
};

const MAX_LISTED = 40;

/** The reference continuity warning; collapses to a chip so it never hides the modal's content. */
const ReferenceContinuityNotice = ({ t, result, onViewIncomes }) => {
    const [expanded, setExpanded] = useState(true);
    if (!result || result.isContinuous) return null;
    const issues = result.issues ?? [];

    if (!expanded) {
        return (
            <XtramButton variant="ghost" small className={styles.issuesChip} onClick={() => setExpanded(true)}>
                <WarningAmberIcon sx={{ fontSize: 16, color: '#b45309' }} />
                {`${issues.length} ${t('ReferenceContinuityIssues')}`}
            </XtramButton>
        );
    }

    return (
        <XtramNotice type="warning">
            <div className={styles.alertHeader}>
                <span>{t('ReferenceContinuityIssues')} ({issues.length})</span>
                <div className={styles.alertActions}>
                    {onViewIncomes && (
                        <XtramButton variant="secondary" small onClick={onViewIncomes}>
                            {t('ViewIncomes')}
                        </XtramButton>
                    )}
                    <XtramButton variant="ghost" small onClick={() => setExpanded(false)}>
                        {t('Hide')}
                    </XtramButton>
                </div>
            </div>
            <span>{t('ReferenceContinuityWarning')}</span>
            {issues.length > 0 && (
                <ul className={styles.issueList}>
                    {issues.slice(0, MAX_LISTED).map((issue, index) => (
                        <li key={index}>{describeIssue(issue, t)}</li>
                    ))}
                    {issues.length > MAX_LISTED && <li><em>+{issues.length - MAX_LISTED} {t('More')}</em></li>}
                </ul>
            )}
        </XtramNotice>
    );
};

export default ReferenceContinuityNotice;
